import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { ReactEditor, useSlate, RenderElementProps } from 'slate-react';
import { Transforms } from 'slate';
import { useGetDocumentTableOfContent } from 'entities/document';
import { Modal } from 'app/components';
import { ImportToC } from 'app/components/editor/import-area';
import { calculatePageOffset } from 'slate/components/page/utils';
import { useEditorSearch } from 'app/router/params';
import { useEditorLocale } from 'app/api/documents/useEditorData';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorLiveSection } from 'app/state/contexts/EditorUiContext';
import { TableOfContentElement } from 'slate/types/slate';

interface TocOverride {
  uuid: string;
  revisionId?: string;
  text?: string | null;
  excluded?: boolean;
}

type TocSettings = {
  start?: string | number;
  stop?: string | number;
  levels: string[];
  overrides: TocOverride[];
  uuid?: string;
  [key: string]: unknown;
};

interface TocHeader {
  uuid: string;
  type: string;
  name?: string;
  pageNumber: number;
  children?: TocHeader[];
}

type TableOfContentProps = {
  element: { settings?: unknown; uuid?: string };
  attributes?: RenderElementProps['attributes'];
  children?: RenderElementProps['children'];
  readOnly?: boolean;
  hasWritePermission?: boolean;
};

export const TableOfContent = ({ element, attributes, readOnly, hasWritePermission, children }: TableOfContentProps) => {
  const tocElement = element as TableOfContentElement;
  const settings = tocElement.settings as TocSettings;
  const { data: pDocument } = useEditorDocument();
  const locale = useEditorLocale();
  const translationId = useEditorSearch().translation;
  const { revisionId: sectionRevisionId } = useEditorLiveSection();
  const { data } = useGetDocumentTableOfContent(pDocument.revisionId, {
    start: settings.start as string,
    stop: settings.stop as string,
    currentPosition: tocElement.uuid,
    headerHierarchy: settings.levels,
    locale,
    sectionRevisionId: translationId ?? sectionRevisionId,
  });
  const [show, setShow] = useState(false);
  const editor = useSlate();
  const pageOffset = calculatePageOffset(pDocument.content?.pageNumbering);
  const handleModalClose = () => {
    setShow(false);
  };

  const handleDelete = () => {
    const path = ReactEditor.findPath(editor, tocElement);
    Transforms.select(editor, path);
    Transforms.removeNodes(editor, { at: path });
  };

  const handleEdit = () => {
    setShow(true);
  };

  return (
    <div className="editor-table-of-content--container" {...attributes}>
      {!readOnly && hasWritePermission && (
        <>
          <div className="controls--container">
            <button onClick={handleEdit}>
              <i className="fa-regular fa-pen-to-square" />
            </button>
            <button className="controls--btn--danger" onClick={handleDelete}>
              <i className="fa-regular fa-trash-can" />
            </button>
          </div>
          <Modal titleLabelKey="toc:insert.modal-title" size="lg" show={show} onClose={handleModalClose}>
            <ImportToC onClose={handleModalClose} defaultSettings={settings} element={tocElement} />
          </Modal>
        </>
      )}
      <div className="editor-table-of-content--content">
        <OrderedList headerList={data?.content as TocHeader[] | undefined} pageOffset={pageOffset} settings={settings} />
      </div>
      {children}
    </div>
  );
};

interface OrderedListProps {
  headerList?: TocHeader[];
  pageOffset?: number;
  settings: TocSettings;
}

const OrderedList = ({ headerList, pageOffset, settings }: OrderedListProps) => {
  const { t } = useTranslation('toc');

  const filteredHeaders = headerList?.filter((header) => settings.levels.includes(header.type)) || [];

  if (filteredHeaders.length === 0) {
    return headerList?.map((header) => <OrderedList key={header.uuid} headerList={header.children} settings={settings} />) || null;
  }

  const headerName = (header: TocHeader) => {
    const overrideHeader = settings.overrides.find((override) => override.uuid === header.uuid);
    if (overrideHeader?.text) {
      return overrideHeader.text;
    }

    return header.name || t('settings.header.no-permission');
  };

  const checkExclude = (header: TocHeader) => {
    const overrideHeader = settings.overrides.find((override) => override.uuid === header.uuid);
    if (overrideHeader && overrideHeader.excluded === true) {
      return true;
    }
    return false;
  };

  const hasValidHeaders = (headers?: TocHeader[]): headers is TocHeader[] => {
    if (!headers || headers.length === 0) return false;
    return headers.some((header) => {
      if (settings.levels.includes(header.type) && !checkExclude(header)) return true;
      return hasValidHeaders(header.children);
    });
  };

  if (!hasValidHeaders(headerList)) return null;

  return (
    <ol style={{ userSelect: 'none' }} contentEditable={false}>
      {headerList.map((header, idx) => {
        if (checkExclude(header) === true) return null;
        return (
          <li key={idx}>
            <div className="toc-item">
              <a href={`#anchor-${header.uuid}`}>
                <span className="toc-header">{headerName(header)}</span>
                <span className="toc-pagenumber">{getPageNumber(header.pageNumber, pageOffset)}</span>
              </a>
            </div>
            <OrderedList headerList={header.children} settings={settings} pageOffset={pageOffset} />
          </li>
        );
      })}
    </ol>
  );
};

const getPageNumber = (pageNumber: number, offset = 0) => {
  const newPageNumber = pageNumber + offset;
  return newPageNumber < 1 ? '' : newPageNumber;
};
